import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import api from '../api';

var Container = styled.div`
  margin-bottom: 1rem;
`;

var ScrollRow = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 12px;
  padding: 8px 0;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

var AlbumCard = styled.div`
  flex-shrink: 0;
  width: 100px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: center;

  &:hover {
    opacity: 0.8;
  }
`;

var Artwork = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
  pointer-events: none;
  -webkit-user-drag: none;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

var AlbumTitle = styled.div`
  font-size: 0.7rem;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #333;
`;

var ArtistName = styled.div`
  font-size: 0.65rem;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var ConnectButton = styled.button`
  background: none;
  color: #fa243c;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 0.75rem;

  &:hover {
    background: #fef2f2;
    border-color: #fa243c;
  }
`;

var SectionLabel = styled.div`
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 6px;
`;

var ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

var ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 360px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

var ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

var ModalArtwork = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
`;

var ModalAlbumInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

var ModalAlbumTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var ModalArtistName = styled.div`
  font-size: 0.85rem;
  color: #888;
`;

var AlbumButton = styled.button`
  width: 100%;
  background: #fa243c;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  cursor: pointer;
  margin-bottom: 16px;

  &:hover {
    background: #d91e34;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

var LookupError = styled.div`
  background-color: tomato;
  color: white;
  border-radius: 8px;
  padding: 0.5rem;
  font-size: 0.75rem;
  margin-bottom: 16px;
`;

var SongDivider = styled.div`
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 8px;
`;

var SongRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f9f9f9;
  }

  &:last-child {
    border-bottom: none;
  }
`;

var SongTitle = styled.div`
  font-size: 0.85rem;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #999;
  padding: 4px 8px;

  &:hover {
    color: #333;
  }
`;

var DRAG_THRESHOLD = 5;

function RecentlyPlayed(props) {
  var onSelect = props.onSelect;
  var musicKit = props.musicKit;

  var scrollRef = useRef(null);
  var dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, dragged: false });

  var handleMouseDown = useCallback(function (e) {
    var el = scrollRef.current;
    if (!el) return;
    dragState.current.isDown = true;
    dragState.current.startX = e.pageX - el.offsetLeft;
    dragState.current.scrollLeft = el.scrollLeft;
    dragState.current.dragged = false;
  }, []);

  var handleMouseLeave = useCallback(function () {
    dragState.current.isDown = false;
  }, []);

  var handleMouseUp = useCallback(function () {
    dragState.current.isDown = false;
  }, []);

  var handleMouseMove = useCallback(function (e) {
    if (!dragState.current.isDown) return;
    e.preventDefault();
    var el = scrollRef.current;
    if (!el) return;
    var x = e.pageX - el.offsetLeft;
    var walk = x - dragState.current.startX;
    if (Math.abs(walk) > DRAG_THRESHOLD) {
      dragState.current.dragged = true;
    }
    el.scrollLeft = dragState.current.scrollLeft - walk;
  }, []);

  var albumsState = useState([]);
  var albums = albumsState[0];
  var setAlbums = albumsState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var pickerState = useState(null);
  var selectedAlbum = pickerState[0];
  var setSelectedAlbum = pickerState[1];

  var lookupLoadingState = useState(false);
  var lookupLoading = lookupLoadingState[0];
  var setLookupLoading = lookupLoadingState[1];
  var lookupErrorState = useState('');
  var lookupError = lookupErrorState[0];
  var setLookupError = lookupErrorState[1];

  var fetchErrorState = useState(null);
  var fetchError = fetchErrorState[0];
  var setFetchError = fetchErrorState[1];

  var isAuthorized = musicKit?.isAuthorized;

  useEffect(
    function () {
      if (!isAuthorized) return;

      setLoading(true);
      setFetchError(null);
      api
        .get('/api/music/recent')
        .then(function (result) {
          setAlbums(result.data || []);
          setLoading(false);
        })
        .catch(function (err) {
          setLoading(false);
          var status = err.response && err.response.status;
          if (status === 401 || status === 403) {
            setFetchError('expired');
          }
        });
    },
    [isAuthorized]
  );

  function handleAlbumPost(album) {
    setLookupLoading(true);
    setLookupError('');
    var lookupParams = { album: album.album_title, artist: album.artist };
    // Apple's recently played feed identifies songs, so the album arrives with
    // one of its song's catalog IDs. Passing it lets the server resolve the
    // album exactly instead of guessing from artist and title text.
    if (album.song_catalog_id) {
      lookupParams.songCatalogID = album.song_catalog_id;
    }

    api
      .get('/api/music/album-lookup', { params: lookupParams })
      .then(function (result) {
        setLookupLoading(false);
        setSelectedAlbum(null);
        var data = result.data;
        onSelect({
          artist: data.artist,
          song_title: data.song_title || null,
          album_title: data.album_title,
          artwork_url: data.artwork_url,
          isrc: data.isrc || null,
          upc: data.upc || null,
          type: data.type,
          release_date: data.release_date || data.releaseDate || '',
        });
      })
      .catch(function () {
        setLookupLoading(false);
        setLookupError("Couldn't find that album. Try searching for it instead.");
      });
  }

  function handleSongSelect(track) {
    setSelectedAlbum(null);
    onSelect({
      artist: track.artist,
      song_title: track.song_title,
      album_title: track.album_title,
      artwork_url: track.artwork_url,
      isrc: track.isrc || null,
      upc: null,
      type: track.type,
      release_date: track.release_date || track.releaseDate || '',
    });
  }

  if (!musicKit || musicKit.isLoading) return null;

  if (!musicKit.isAuthorized) {
    return (
      <Container style={{ display: 'flex', justifyContent: 'center' }}>
        <ConnectButton
          onClick={function () {
            musicKit.authorize();
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fa243c"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
          Connect Apple Music for quick suggestions
        </ConnectButton>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <SectionLabel>Recently Played</SectionLabel>
        <div style={{ fontSize: '0.8rem', color: '#999' }}>Loading...</div>
      </Container>
    );
  }

  if (fetchError === 'expired') {
    return (
      <Container style={{ display: 'flex', justifyContent: 'center' }}>
        <ConnectButton
          onClick={function () {
            musicKit.authorize();
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fa243c"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
          Reconnect Apple Music for suggestions
        </ConnectButton>
      </Container>
    );
  }

  if (albums.length === 0) return null;

  return (
    <Container>
      <SectionLabel>Recently Played</SectionLabel>
      <ScrollRow
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {albums.map(function (album, index) {
          var artUrl = album.artwork_url
            ? album.artwork_url.replace('{w}', '200').replace('{h}', '200')
            : '';

          return (
            <AlbumCard
              key={index}
              onClick={function () {
                if (dragState.current.dragged) return;
                if (album.tracks?.length === 1) {
                  handleSongSelect(album.tracks[0]);
                } else {
                  setLookupError('');
                  setSelectedAlbum(album);
                }
              }}
            >
              <Artwork src={artUrl} alt={album.album_title} />
              <AlbumTitle>{album.album_title}</AlbumTitle>
              <ArtistName>{album.artist}</ArtistName>
            </AlbumCard>
          );
        })}
      </ScrollRow>

      {selectedAlbum && (
        <ModalOverlay
          onClick={function (e) {
            if (e.target === e.currentTarget) {
              setLookupError('');
              setSelectedAlbum(null);
            }
          }}
        >
          <ModalContent>
            <CloseButton
              onClick={function () {
                setLookupError('');
                setSelectedAlbum(null);
              }}
            >
              &#x2715;
            </CloseButton>
            <ModalHeader>
              <ModalArtwork
                src={
                  selectedAlbum.artwork_url
                    ? selectedAlbum.artwork_url.replace('{w}', '200').replace('{h}', '200')
                    : ''
                }
                alt={selectedAlbum.album_title}
              />
              <ModalAlbumInfo>
                <ModalAlbumTitle>{selectedAlbum.album_title}</ModalAlbumTitle>
                <ModalArtistName>{selectedAlbum.artist}</ModalArtistName>
              </ModalAlbumInfo>
            </ModalHeader>

            <AlbumButton
              disabled={lookupLoading}
              onClick={function () {
                handleAlbumPost(selectedAlbum);
              }}
            >
              {lookupLoading ? 'Looking up album...' : 'Post about this album'}
            </AlbumButton>

            {lookupError && <LookupError>{lookupError}</LookupError>}

            {selectedAlbum.tracks?.length > 0 && (
              <div>
                <SongDivider>Or pick a song:</SongDivider>
                {selectedAlbum.tracks.map(function (track, i) {
                  return (
                    <SongRow
                      key={i}
                      onClick={function () {
                        handleSongSelect(track);
                      }}
                    >
                      <SongTitle>{track.song_title || 'Unknown Track'}</SongTitle>
                    </SongRow>
                  );
                })}
              </div>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default RecentlyPlayed;
